/** Waste reasons tab - quantity, cost, frequency and trend for each waste reason, split by
 * avoidable vs unavoidable so procurement/ops can prioritise the addressable causes first. */

import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../components/ui/Section';
import { applyRoundAndFormat } from '../../../utils';
import { WASTE_REASON_ROWS } from '../data';
import { StatusChip } from './StatusChip';

interface ReasonsTabProps {
  currencyCode?: string;
}

const TrendCell = ({ value }: { value: number }) => {
  const status = value > 5 ? 'critical' : value < -2 ? 'good' : 'warning';
  const arrow = value > 0 ? '\u2191' : value < 0 ? '\u2193' : '\u2192';
  return <StatusChip status={status}
    label={`${arrow} ${Math.abs(value).toFixed(1)}%`} />;
};

const ReasonsTab = ({ currencyCode }: ReasonsTabProps) => {
  const { t } = useTranslation('common');
  const sorted = [...WASTE_REASON_ROWS].sort((a, b) => b.wasteCost - a.wasteCost);
  const totalCost = sorted.reduce((sum, r) => sum + r.wasteCost, 0);

  return (
    <Section>
      <Box sx={{ p: 4 }}>
        <Typography variant="textMD"
          fontWeight={600}
          sx={{ mb: 1 }}>
          {t('wasteDashboardReasonsTitle', 'Waste reasons')}
        </Typography>
        <Typography variant="textSM"
          color="text.secondary"
          sx={{ mb: 3, display: 'block' }}>
          {t('wasteDashboardReasonsSubtitle', 'Quantity, cost, frequency and trend for each recorded waste reason, ranked by cost.')}
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('wasteDashboardReason', 'Reason')}</TableCell>
              <TableCell>{t('wasteDashboardAvoidability', 'Avoidable / unavoidable')}</TableCell>
              <TableCell align="right">{t('wasteDashboardWasteCost', 'Cost')}</TableCell>
              <TableCell align="right">{t('wasteDashboardShare', 'Share of total')}</TableCell>
              <TableCell align="right">{t('wasteDashboardWasteQuantity', 'Quantity (kg)')}</TableCell>
              <TableCell align="right">{t('wasteDashboardFrequency', 'Frequency')}</TableCell>
              <TableCell>{t('wasteDashboardTrend', 'Trend vs prior period')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row) => (
              <TableRow key={row.reason}>
                <TableCell>{row.reason}</TableCell>
                <TableCell>
                  <StatusChip status={row.avoidable ? 'critical' : 'good'}
                    label={row.avoidable ? t('wasteDashboardAvoidable', 'Avoidable') : t('wasteDashboardUnavoidable', 'Unavoidable')} />
                </TableCell>
                <TableCell align="right">{applyRoundAndFormat(row.wasteCost, currencyCode)}</TableCell>
                <TableCell align="right">{((row.wasteCost / totalCost) * 100).toFixed(1)}%</TableCell>
                <TableCell align="right">{row.wasteQuantityKg.toLocaleString()}</TableCell>
                <TableCell align="right">{row.frequency}</TableCell>
                <TableCell><TrendCell value={row.trendVsPriorPeriod} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Section>
  );
};

export default ReasonsTab;
